import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, share, shareReplay, tap } from 'rxjs/operators';
import { HALResponse } from 'ng/src/app/shared/model/app.model';
import { LogBookUser, LogBookUserEmail, LogBookUserLink, LogUser, LogUserGroup, LogUserProjectionType, User } from 'ng/src/app/users/shared/user.model';
import { GeneralParameters, LogUserProjections } from "app/shared/model/request-parameter.model";
import { PagedEntity } from "app/shared/model/app.model";
import { AppStateService } from "ng/src/app/core/services/app-state.service";

@Injectable({
	providedIn: 'root'
})
export class UserService {
	private historicalLogUsers: { [logId: number]: Observable<LogBookUserLink[]> } = {};
	private _unverifiedEmails: string[] = [];

	constructor(
		private http: HttpClient
	) { }

	get unverifiedEmails(): string[] {
		return [...this._unverifiedEmails];
	}
	
	verifyEmail(email: string): Observable<boolean> {
		return this.http.get<boolean>('/api/validateUserEmail', { params: { email } });
	}
	
	getLogUsers(logId: number, ignoreLogAccess = false) {
		let headers = new HttpHeaders();
		if (ignoreLogAccess) {
			headers = headers.set('X-LogBook-Flags','ignore-log-access');
		}
		const params: GeneralParameters = {
			log: logId
		}
		return this.http.get<HALResponse<{ logUsers: LogUser[] }>>('/api/logUsers', { params: params, headers })
			.pipe(map(res => PagedEntity.from(res._embedded.logUsers, res.page)))
	}

	getLogUserGroupMembers(groupId: number) {
		return this.http.get<HALResponse<{ logUserGroupMembers: LogUserGroup[] }>>('/api/logUserGroupMembers', {
			params: {
				group: groupId.toString(),
				useSpinner: 'true'
			}
		})
		.pipe(
			map(r => r._embedded.logUserGroupMembers)
		);
	}
	
	fetchLogUsers<T extends LogUserProjections>(params: LogUser.Query, projection?: T) {
		params = {
			...params,
			'projection': projection ?? LogUserProjections.BASE
		}
		return this.http.get<HALResponse<{ logUsers: LogUserProjectionType<T>[] }>>('/api/logUsers', { params: params })
			.pipe(map(res => PagedEntity.from(res._embedded.logUsers, res.page)))
	}
	
	fetchLogBookUsers(ignoreUserAccess: boolean = true, id: number[]) {
		let headers = new HttpHeaders();
		if (ignoreUserAccess) {
			headers = headers.set('X-LogBook-Flags', 'ignore-user-access');
		}
		
		return this.http.get<HALResponse<{logBookUsers: LogBookUser[]}>>(`/api/logBookUsers`, {
				params: { id },
				headers
		})
			.pipe(map(resp => PagedEntity.from(resp._embedded.logBookUsers, resp.page)));
	}
	
	fetchLogUserGroups(params: GeneralParameters) {
		return this.http.get<HALResponse<{ logUserGroups: LogUserGroup[] }>>('/api/logUserGroups', { params })
			.pipe(map(res => PagedEntity.from(res._embedded.logUserGroups, res.page)));
	}

	getHistoricalLogUsersByLogId(logId: number) {
		if (!this.historicalLogUsers[logId]) {
			this.historicalLogUsers[logId] = this.http.get<HALResponse<{ logBookUsers: LogBookUserLink[] }>>(
				`api/logBookUsers?historicalLogUser=${logId}`
			)
			.pipe(
				map(r => r._embedded.logBookUsers),
				shareReplay(1)
			);
		}

		return this.historicalLogUsers[logId];
	}

	getLogBookUserEmails(separator = ', ', userIds: number[] = [], oldStyleInvocation: boolean = true) {
		let params = new HttpParams().set('size', '10000');
		userIds.forEach(id => params =  params.append('userDetails', id.toString()));
		return this.http.get<HALResponse<{ logBookUserEmails: LogBookUserEmail[] }>>(
			'/api/logBookUserEmails',
			{
				params: params,
				headers: new HttpHeaders().set("X-LogBook-Flags","ignore-user-access")
			}
		)
		.pipe(
			map(r => {
				if (userIds && userIds.length === 1 && oldStyleInvocation )
					return r._embedded.logBookUserEmails;
				
				const emails: LogBookUserEmail[] = [],
					  emailUserMap: { [userId: number]: LogBookUserEmail } = {};

				r._embedded.logBookUserEmails.forEach(email => {
					if (emailUserMap[email.userId])
						emailUserMap[email.userId].addressText += separator + email.email;
					else {
						emailUserMap[email.userId] = email;
						email.addressText = email.email;

						emails.push(email);
					}
				});

				return emails;
			}),
			tap(emails => {
				if (userIds && userIds.length === 1 && oldStyleInvocation)
					this._unverifiedEmails = emails.filter(e => e.hiddenLogInvitations).map(e => e.email);
			})
		);
	}
	
	fetchLogBookUserEmails(userIds: number[] = []): Observable<LogBookUserEmail[]> {
		return this.http.get<HALResponse<{ logBookUserEmails: LogBookUserEmail[] }>>('/api/logBookUserEmails', {
			headers: new HttpHeaders().set("X-LogBook-Flags","ignore-user-access"),
			params: {
				userDetails: userIds
			} as GeneralParameters
		})
			.pipe(
				map(resp => resp._embedded.logBookUserEmails)
			)
	}

	updateDefaultLog(userId: number, defaultLogId: number) {
		return this.http.patch<User>(`/api/logBookUserDetails/${userId}`, {
			config: {
				defaultLog: defaultLogId
			}
		});
	}
	
	updateLastSeenSystemNotificationDate(userId: number, type: keyof Pick<User, 'dateOfLastViewedSystemNotification' | 'dateOfLastDeliveredSystemNotification'>, date: string | Date) {
		return this.http.patch<User>(`/api/logBookUserDetails/${userId}`, {
			[type]: date
		})
			.pipe(
				map(r => {
					if (AppStateService.currentUser?.id === userId) {
						AppStateService.currentUser.dateOfLastViewedSystemNotification = r.dateOfLastViewedSystemNotification;
						AppStateService.currentUser.dateOfLastDeliveredSystemNotification = r.dateOfLastDeliveredSystemNotification;
						AppStateService.currentUser.updated = r.updated;
						AppStateService.currentUser = AppStateService.currentUser;
					}
					
					return r.dateOfLastViewedSystemNotification;
				}),
				share()
			)
	}

	getLogBookUserDetails(first: number = 0, rows: number = 12, userSearch: string | undefined = undefined, sort: string[] = ['user.lastName,asc']) {
		let params = new HttpParams()
			.set('size', rows.toString())
			.set('page', (first/rows).toString());
		if(userSearch)
			params = params.set('globalUserSearch', userSearch);
		sort.forEach(val => params = params.append('sort', val));
		
		return this.http.get<HALResponse<{logBookUserDetails: User[]}>>(
			'/api/logBookUserDetails',
			{
				params: params,
				headers: new HttpHeaders().set("X-LogBook-Flags", "ignore-user-access")
			});
	}
	
	saveLogBookUserDetails(href: string, userDetails : Partial<User>) {
		let headers = new HttpHeaders().set("X-LogBook-Flags", "ignore-user-access");
		return this.http.patch<User>(href, userDetails, {headers: headers});
	}
}
